<template>
  <section class="datatable-wrapper">
    <div class="d-flex align-items-center" v-if="!isOnWidgetMode">
      <h3>{{ title || alias }}</h3>
    </div>

    <b-row v-if="enableGeoSearch" class="mb-2">
      <LmGooglePlacesAutocomplete
        class="col-12"
        :label="$t('address')"
        :value="searchAddress"
        @placeChanged="placeChanged"
        @inputLmGooglePlacesAutocomplete="inputLmGooglePlacesAutocomplete"
      />
      <div class="col-12">
        <a-slider
          :disabled="hasBeenGeoSearched"
          :default-value="geoSliderOptions.defaultDistance"
          :v-model="distance"
          :step="100"
          :marks="geoSliderOptions.distanceMarks"
          :max="geoSliderOptions.maxDistance"
          @afterChange="distanceChanged"
        />
      </div>
      <LmLeaFletMap
        :markerLatLng="markerLatLng"
        :markerObjects="markerObjects"
        :centerMapCoordinate="markerLatLng"
        :zoom="defaultZoomLevel"
        class="col-12"
        :height="mapOptionsHeight"
        />
    </b-row>

    <Searcher
      v-if="!isOnWidgetMode && table.searcher && !hideSearcher"
      :hideFilters="!(!!table.filters)"
      :options="table.searcher"
      :mode="mode"
      :enableCsvExport="enableCsvExport"
      @onSearch="onSearch"
      @toggleFilters="filtersCollpse = !filtersCollpse"
      @generateCsv="onGenerateCsv"
    />

    <b-collapse v-model="filtersCollpse" class="filtersCollpse">
      <div
        class="d-flex align-items-center"
        :class="{
          'justify-content-end': !table.filters,
          'justify-content-between': table.filters
        }"
      >
        <Filters
          v-if="!isOnWidgetMode && table.filters && !hideFilters"
          :filters="table.filters"
          :slug="slug"
          :search="search"
          :filtersToApply="filtersToApply"
          :queryString="queryString"
          @change="onFilter"
        />
        <Ordering
          v-if="sortableColumns.length && mode === 'box'"
          :columns="sortableColumns"
          @change="onSortChange"
        />
      </div>
    </b-collapse>

    <b-table
      v-if="mode === 'list'"
      v-bind="table"
      thead-class="text-nowrap"
      class="scroll"
      :key="key"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @row-selected="onRowSelected"
      @sort-changed="onSortTableChange"
    >

      <!-- Columnas -->
      <template #table-colgroup="scope">
        <col
          v-for="field in scope.fields"
          :key="field.key"
          :style="field.columnStyles"
          :class="{'primary-column': field.primaryColumn}"
          class="border-b border-right border-left"
        >
      </template>

      <!-- Celdas -->
      <template #cell()="data">
        <Component
          :is="cellComponent(data)"
          :value="data.value"
          :showDetails="data.item._showDetails"
          @details="data.toggleDetails(); toggleDetails($event)"
          v-bind="cellProps(data)"
          class="cell"
        />
      </template>
      <template #cell(selected)="{ rowSelected }">
        <template v-if="rowSelected">
          <span aria-hidden="true">&check;</span>
        </template>
        <template v-else>
          <span aria-hidden="true">&nbsp;</span>
        </template>
      </template>

      <!-- Header -->
      <template #head(selected)="data">
        <b-form-checkbox
          @input="onSelectAllRows(data, $event)"
        ></b-form-checkbox>
      </template>

      <!-- Estados -->
      <template #table-busy>
        <div class="text-center text-secondary my-2">
          <b-spinner class="align-middle mr-4"></b-spinner>
          <strong>Cargando...</strong>
        </div>
      </template>
      <template #empty>
        <LmSvgIcon
          wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column p-4"
          width="6em"
          icon="LmNoDataIcon"
          text="No se encontraron resultados, prueba con otra busqueda."
        />
      </template>

      <!-- Details -->
      <template #row-details>
        <div>
          <component :is="detailComponent" :value="detailValue"></component>
        </div>
      </template>
    </b-table>

    <template v-else-if="mode === 'box'">
      <b-row v-if="table.items.length" style="row-gap: 20px;" class="mb-4">
        <b-col
          v-for="(item, index) in table.items"
          :key="index"
          :sm="primaryColumn.boxCols"
          cols="12"
        >
          <b-link class="text-dark" :to="item[primaryColumn.key].click">
            <b-card class="item-box" no-body :style="`border-top: 5px solid ${tableAccentColor}; height: 100%; max-height: 175px`">
              <Component
                :is="primaryColumn.component.value"
                :value="item[primaryColumn.key]"
                :config="primaryColumn.boxOptions"
                v-bind="primaryColumn.component.props"
                isBoxMode
              />
            </b-card>
          </b-link>
        </b-col>
      </b-row>
      <div v-else>
        <LmSvgIcon
          wrapperClass="d-flex h-100 justify-content-center align-items-center flex-column p-4"
          width="6em"
          icon="LmNoDataIcon"
          text="No se encontraron resultados, prueba con otra busqueda."
        />
      </div>
    </template>

    <div class="flex items-center mt-2" v-if="!isOnWidgetMode && itemsCount > 1">
      <span class="d-none d-sm-block mr-3">
        Mostrando {{ pagination.from }} a {{ pagination.to }} de {{ itemsCount }}
      </span>
      <b-pagination
        v-model="currentPage"
        :total-rows="itemsCount"
        :per-page="size"
        @input="loadTableItems"
      ></b-pagination>
    </div>
    <Buttons
      v-if="!isOnWidgetMode && !hideButtons && table.buttons && table.buttons.length && mode !== 'box'"
      :buttons="table.buttons"
      :showCancelButton="showCancelButton"
      :showMultipleSignButton="showMultipleSignButton"
      :showReloadButton="showReloadButton"
      :valuesSelected="dataValue?.length? dataValue : []"
      :isOnPreviewMode="isOnPreviewMode"
      :isOnModeBox="isOnModeBox"
      class="buttons"
      @onRefreshTable="refreshTable"
      @onReloadTable="loadTableItems"
    />

  </section>
</template>

<script>
// Components
import lmToasts from '@/mixins/lmToasts'
import lmDocumentsMixin from '@/mixins/lmDocumentsMixin.js'

// Cells
import Badge from './components/cells/Badge'
import Keyword from './components/cells/Keyword'
import Number from './components/cells/Number'
import Actions from './components/cells/Actions'
import Avatar from './components/cells/Avatar'
import Profile from './components/cells/Profile'
import Date from './components/cells/Date'
// Documents
import DocumentNameStatus from './components/cells/documents/NameStatus'
import DocumentSigners from './components/cells/documents/Signers'
import DocumentCollaborators from './components/cells/documents/Collaborators'
import DocumentProgress from './components/cells/documents/Progress'
import DocumentSize from './components/cells/documents/Size'
import DocumentPrimary from './components/cells/documents/Primary'
import DocumentRecipients from './components/cells/documents/Recipients'
import DocumentSignatureType from './components/cells/documents/SignatureType'

// Processes
import ProcessPrimary from './components/cells/processes/Primary'
import ProcessLocation from './components/cells/processes/Location'

// Process Types
import ProcessTypePrimary from './components/cells/processtypes/Primary'

// Document Types
import DocumentTypePrimary from './components/cells/documenttypes/Primary'

// Users
import UserPrimary from './components/cells/users/Primary'

// Areas
import AreaPrimary from './components/cells/areas/Primary'

import Searcher from './components/Searcher'
import Filters from './components/Filters'
import Ordering from './components/Ordering'
import Buttons from './components/Buttons'

// Utils
import commonsApi from '@/api/commons'

// Vuex
import { mapGetters } from 'vuex'

// Maps
import LmGooglePlacesAutocomplete from '@/components/LmGooglePlacesAutocomplete.vue'
import LmLeaFletMap from '@/components/LmLeaFletMap.vue'

export default {
  mixins: [lmToasts, lmDocumentsMixin],
  props: {
    slug: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: null
    },
    isOnWidgetMode: {
      type: Boolean,
      default: false
    },
    hideButtons: {
      type: Boolean,
      default: false
    },
    queryString: {
      type: String,
      default: null
    },
    isOnPreviewMode: {
      type: Boolean,
      default: false
    },
    previewData: {
      type: Object,
      default: () => {}
    },
    hideSearcher: {
      type: Boolean,
      default: false
    },
    hideFilters: {
      type: Boolean,
      default: false
    }
  },
  components: {
    Searcher,
    Filters,
    Ordering,
    Buttons,
    // Cells
    Badge,
    Keyword,
    Number,
    Actions,
    Avatar,
    Profile,
    Date,
    // Documents
    DocumentPrimary,
    DocumentNameStatus,
    DocumentSigners,
    DocumentCollaborators,
    DocumentProgress,
    DocumentSize,
    DocumentRecipients,
    DocumentSignatureType,
    // Processes
    ProcessPrimary,
    ProcessLocation,
    // Process Types
    ProcessTypePrimary,
    // Document Types
    DocumentTypePrimary,
    // Users
    UserPrimary,
    // Areas
    AreaPrimary,
    'LoginHistory': () => import('./components/cells/users/components/LoginHistory'),
    'Audit': () => import('./components/cells/users/components/Audit'),
    'UserDetails': () => import('./components/cells/users/UserDetails'),
    'AreaDetails': () => import('./components/cells/areas/AreaDetails'),
    LmGooglePlacesAutocomplete,
    LmLeaFletMap
  },
  provide () {
    const context = {}
    for (const key in this.context) {
      Object.defineProperty(context, key, {
        enumerable: true,
        get: () => this.context[key]
      })
    }
    return { context }
  },
  data () {
    return {
      dataValue: null,
      key: 0,
      currentPage: 1,
      name: '',
      alias: '',
      size: 10,
      tableAccentColor: this.accentColor,
      mode: 'list',
      enableCsvExport: false,
      showMultipleSignButton: false,
      showCancelButton: true,
      itemsCount: 0,
      detailComponent: null,
      detailValue: null,
      table: {
        striped: true,
        borderless: false,
        bordered: false,
        hover: true,
        showEmpty: true,
        selectable: false,
        selectMode: 'single',
        responsive: true,
        busy: false,
        fields: [],
        items: [],
        filters: null,
        searcher: null,
        buttons: null,
        noLocalSorting: true
      },
      search: {},
      filtersToApply: [],
      sortRulesToApply: [],
      filtersCollpse: true,
      serviceSource: null,
      orderingRules: [],
      sortBy: null,
      sortDesc: false,
      showReloadButton: false,
      enableGeoSearch: false,
      geoSearcher: {},
      markerLatLng: [],
      geoSearchResults: [],
      geoQuery: {
        location: {},
        distance: null
      },
      distance: 500,
      searchAddress: null
    }
  },
  watch: {
    slug: {
      async handler (val) {
        try {
          await this.loadTable()
          this.refreshTable()
        } catch (error) {
          console.log(error)
        }
      }
    },
    async queryString (val) {
      await this.loadTableItems()
    }
  },
  computed: {
    ...mapGetters('settings', ['accentColor']),
    context () {
      return {
        serviceSource: this.serviceSource
      }
    },
    pagination () {
      return {
        from: ((this.size * this.currentPage) - this.size) + 1,
        to: this.itemsCount < this.size ? this.itemsCount : this.size * this.currentPage
      }
    },
    primaryColumn () {
      const box = this.table.fields.find(el => el?.primaryColumn)

      const esKeys = Object.entries(box.elastic_key)

      if (box?.boxOptions) {
        for (const prop of esKeys) {
          if (prop.includes(box.boxOptions.boxTitleKey)) {
            box.boxOptions.boxTitleKey = prop.at(0)
          }
          if (prop.includes(box.boxOptions.boxContentKey)) {
            box.boxOptions.boxContentKey = prop.at(0)
          }
        }
      }

      return box
    },
    sortableColumns () {
      return this.table.fields.filter(el => el?.sortable) || []
    },
    markerObjects () {
      return this.geoSearchResults.items?.length ? this.geoSearchResults.items : []
    },
    hasBeenGeoSearched () {
      return this.searchAddress === null
    },
    geoSliderOptions () {
      if (this.geoSearcher?.geoSliderOptions) {
        return this.geoSearcher.geoSliderOptions
      } else {
        return {
          geoSliderOptions: {
            defaultDistance: 500,
            maxDistance: 2000,
            distanceMarks: {
              100: '100m',
              500: '500m',
              1000: '1000m',
              1500: '1500m',
              2000: '2000m'
            }
          }
        }
      }
    },
    mapOptionsHeight () {
      if (this.geoSearcher?.mapOptions?.height) {
        return this.geoSearcher.mapOptions.height
      } else {
        return '250px'
      }
    },
    defaultZoomLevel () {
      if (this.geoSearcher?.mapOptions?.defaultZoomLevel) {
        return this.geoSearcher.mapOptions.defaultZoomLevel
      } else {
        return '15'
      }
    },
    isOnModeBox () {
      return this?.mode === 'box'
    }
  },
  methods: {
    toggleDetails ({ component, value }) {
      this.detailComponent = component
      this.detailValue = value
    },
    onSelectAllRows (data, event) {
      if (event) {
        data.selectAllRows()
      } else {
        data.clearSelected()
      }
    },
    onRowSelected (event) {
      this.dataValue = event
      this.$emit('onSelect', event)
    },
    refreshTable () {
      // NOTA. necesito refrescar la tabla cada vez
      // que se actualizan los items por fallas de renderizado
      // TODO. Buscar otra mejor solucion
      this.key++
      this.dataValue = []
    },
    cellProps (event) {
      const props = event.field?.component?.props
      if (props) {
        return props
      }
      return {}
    },
    cellComponent (event) {
      return event.field?.component?.value || 'Keyword'
    },
    async onGenerateCsv (event) {
      this.lmModalInformationMessage({
        modalClosable: false,
        modalKeyboard: false,
        resultSubtitle: '',
        buttons: [
          {
            btnText: this.$t('cancel'),
            btnType: 'danger',
            btnGhost: true,
            btnCallback: () => {
              this.isProcessing = false
            }
          },
          {
            btnText: this.$t('accept'),
            btnCallback: () => {
              this.generateCsv()
            }
          }
        ],
        message: this.$t('csv_generate_confirmation', {
          name: this.alias,
          count: this.itemsCount
        })
      })
    },
    async generateCsv (event) {
      await commonsApi.axiosPostWrapper('/datatables/generate-csv', {
        slug: this.slug,
        search: this.search,
        filters: this.filtersToApply,
        sort: this.sortRulesToApply,
        queryString: this.queryString
      })
      this.lmNotificationSuccessMessage({
        description: this.$t('wait_for_csv_completion')
      })
    },
    async onSearch (event) {
      this.search = event
      await this.loadTableItems()
    },
    async onFilter (event) {
      this.filtersToApply = event
      await this.loadTableItems()
    },
    getElasticKeyToSort (item) {
      return item?.sortableKey || Object.values(item.elastic_key).at(0)
    },
    async onSortChange (event) {
      this.sortRulesToApply = event
      await this.loadTableItems()
    },
    async onSortTableChange (event) {
      const { sortBy, sortDesc } = event
      const column = this.sortableColumns.find(el => el.key === sortBy)
      const key = this.getElasticKeyToSort(column)
      const rule = {
        [key]: sortDesc ? 'desc' : 'asc'
      }
      this.orderingRules.push(rule)
      // TODO. en modo tabla solo me deja ordenar de un valor a la vez
      // por el tema de los iconos del th
      // sortBy solo deja de una columna a la vez
      this.sortBy = sortBy
      this.sortDesc = sortDesc
      await this.onSortChange([rule])
    },
    async loadTable () {
      let res
      if (this.isOnPreviewMode) {
        // En modo preview obtenemos los datos via la prop previewData, ya que a esta altura
        // la tabla de datos aun no existe o no tiene guardados los últimos cambios en mongo.
        res = { data: this.previewData }
        res.data.table.fields = res.data.table.fields.filter(i => i.visible)
        this.filtersToApply = res.data.filtersToApply
        this.serviceSource = res.data.table.origin.id || null
      } else {
        res = await commonsApi.axiosGetWrapper(`/datatables/${this.slug}/table`)
        this.serviceSource = res.data.serviceSource || null
      }
      this.name = res.data.name
      this.alias = res.data.alias
      this.size = res.data.size
      this.mode = res.data.mode
      this.table.selectable = res.data.selectable
      this.table.selectMode = res.data.selectMode
      this.table.fields = res.data.table.fields
      this.table.filters = res.data.table.filters || null
      this.table.buttons = res.data.table.buttons || null
      this.table.searcher = res.data.table.searcher || null
      this.tableAccentColor = res.data.accentColor || this.accentColor
      this.enableCsvExport = res.data.enableCsvExport
      this.enableGeoSearch = res.data?.enableGeoSearch || false
      this.showMultipleSignButton = res.data.showMultipleSignButton
      this.showCancelButton = res.data.showCancelButton
      this.geoSearcher = res.data.builder?.geoSearcher || {}
      if (this.geoSearcher?.geoSliderOptions?.defaultDistance) {
        this.distance = this.geoSearcher.geoSliderOptions.defaultDistance
      }

      if (this.table.selectable) {
        this.table.fields.unshift({
          key: 'selected',
          label: 'Seleccionado'
        })
      }
    },
    async loadTableItems () {
      try {
        this.table.busy = true
        if (!this.isOnPreviewMode && !this.isOnWidgetMode) {
          this.unSubscribeToDocumentsEvents()
        }
        const res = await commonsApi.axiosPostWrapper('/datatables/items', {
          slug: this.slug,
          page: this.currentPage,
          search: this.search,
          filters: this.filtersToApply,
          sort: this.sortRulesToApply,
          queryString: this.queryString,
          geoQuery: this.geoQuery
        })
        this.table.items = res.data.items
        this.itemsCount = res.data.count
        if (!this.isOnPreviewMode && !this.isOnWidgetMode) {
          this.subscribeToDocumentsEvents()
        }
        this.refreshTable()
      } catch (error) {
        console.log(error)
      } finally {
        this.table.busy = false
      }
    },

    distanceChanged (val) {
      this.distance = val
      this.geoQuery.distance = `${this.distance}m`
      this.geoSearch()
    },

    placeChanged (place) {
      const latLong = [
        place.geometry.location.lat(),
        place.geometry.location.lng()
      ]
      this.markerLatLng = latLong
      this.searchAddress = place.formatted_address
      this.geoQuery = {
        location: {
          lat: latLong[0],
          lon: latLong[1]
        },
        distance: `${this.distance}m`
      }
      this.geoSearch()
    },

    async geoSearch () {
      try {
        this.loadTableItems()
        try {
          const res = await commonsApi.axiosPostWrapper(`${this.serviceSource}/datatables-geoquery`, {
            slug: this.slug,
            page: this.currentPage,
            search: this.search,
            filters: this.filtersToApply,
            queryString: this.queryString,
            geoQuery: this.geoQuery
          })
          this.geoSearchResults = res.data
        } catch (error) {
          this.geoSearch = []
        }
      } catch (error) {
        console.log(error)
      }
    },

    inputLmGooglePlacesAutocomplete (value) {
      if (!value) {
        this.markerLatLng = []
        this.geoSearchResults = []
        this.geoQuery = {}
        this.searchAddress = null
        this.loadTableItems()
      }
    }
  },
  async created () {
    try {
      await this.loadTable()
      await this.loadTableItems()
    } catch (error) {
      console.log(error)
    }
  }
}
</script>

<style lang="sass" scoped>
.item-box
  transition: all .4s ease-in-out
  &:hover
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15)
.primary-column
  border-right: 1px solid #17a2b842
  border-left: 1px solid #17a2b842
.edit-table-button
  color: #afafaf
  margin-left: 1rem
  font-size: 1.25rem
.scroll
    flex-wrap: nowrap
    overflow-x: auto
    padding-bottom: 0.5rem
    &:hover
      &::-webkit-scrollbar-thumb
        border-radius: 20px
        background: #888
    &::-webkit-scrollbar
      height: 8px
      width: 8px
    &::-webkit-scrollbar-thumb
      border-radius: 20px
      background: #888
    &::-webkit-scrollbar-thumb:hover
      background: #555
.cell
  margin: 2px 0;
</style>
<style>
.table td {
  vertical-align: middle;
}
.filtersCollpse{
  margin-bottom: 15px;
}
.card {
  margin-bottom: 9px !important
}
</style>
